import React, { ReactNode } from 'react';

/**
 * A short aside attached to a field or a section — a rule the customer needs
 * to know before they answer, or a consequence of what they just answered.
 *
 * Exists because the booking flow had six of these hand-rolled as inline
 * `style={{ backgroundColor: '#f0f7ff', border: '1px solid #b8daff' }}` blocks
 * on CollectionDelivery alone, each a slightly different Bootstrap-ish blue,
 * amber or grey — four competing accents against DESIGN_LANGUAGE.md §3, and
 * none of them themeable. One component, three tones, brand tokens only.
 *
 * `info` is the default and is the brand tone: it carries anything the
 * customer simply needs to read. `warning` is amber and is reserved for a
 * genuine consequence (a surcharge, a quote that will not hold). `note` is
 * slate and is for fine print that qualifies rather than warns.
 */
export type CalloutTone = 'info' | 'warning' | 'note';

interface CalloutProps {
  tone?: CalloutTone;
  /** Short label above the body. Optional — a one-line callout rarely needs one. */
  title?: ReactNode;
  children: ReactNode;
  /**
   * Announce the callout when it appears in response to something the
   * customer did (the different-city warning). Polite by design: this is
   * never an error the submit is blocked on, so it must not interrupt.
   */
  live?: boolean;
  className?: string;
}

function ToneIcon({ tone }: { tone: CalloutTone }): JSX.Element {
  const common = {
    className: 'kk-callout__icon',
    viewBox: '0 0 20 20',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  };

  if (tone === 'warning') {
    return (
      <svg {...common}>
        <path d="M10 2.8 18.2 17H1.8L10 2.8Z" />
        <path d="M10 8v3.4" />
        <path d="M10 14.2h.01" />
      </svg>
    );
  }

  if (tone === 'note') {
    return (
      <svg {...common}>
        <path d="M4 3.2h8.4L16 6.8V16.8H4V3.2Z" />
        <path d="M12 3.2v3.8h3.8" />
        <path d="M6.8 10.6h6.4M6.8 13.4h4.2" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <circle cx="10" cy="10" r="7.6" />
      <path d="M10 9.2v4.4" />
      <path d="M10 6.6h.01" />
    </svg>
  );
}

export default function Callout({
  tone = 'info',
  title,
  children,
  live = false,
  className,
}: CalloutProps): JSX.Element {
  return (
    <div
      className={`kk-callout kk-callout--${tone}${
        className ? ` ${className}` : ''
      }`}
      {...(live ? { role: 'status', 'aria-live': 'polite' as const } : {})}
    >
      <ToneIcon tone={tone} />
      <div className="kk-callout__body">
        {title && <strong className="kk-callout__title">{title}</strong>}
        {children}
      </div>
    </div>
  );
}
